import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { color_colors_gold, color_colors_ocean, color_shades_darker, color_shades_darkest, color_variants_ocean_light_1 } from '../../constants/colors'
import { MenuSyncStatus } from '../../hooks/MenuContainer'
import { describeDifferences, LocationSyncResult } from '../../utils/locationMenuSync'

interface LocationSyncDetailsProps {
    status: MenuSyncStatus
    locations: LocationSyncResult[]
}

const TICKER_INTERVAL_MS = 1800;
const MAX_ISSUE_CHIPS = 4;
const MAX_ITEMS_IN_TOOLTIP = 8;

// Same menu page, for another location: swaps the application id in front of "/menu"
const getLocationMenuUrl = (liveApplicationId: string) =>
    window.location.pathname.replace(/[^/]+\/menu(\/.*)?$/, `${liveApplicationId}/menu`);

const listItems = (label: string, names: string[] = []) => names.length ?
    `${label}: ${names.slice(0, MAX_ITEMS_IN_TOOLTIP).join(", ")}${names.length > MAX_ITEMS_IN_TOOLTIP ? ` and ${names.length - MAX_ITEMS_IN_TOOLTIP} more` : ""}` : "";

const getIssueTooltip = (location: LocationSyncResult) => [
    `Open ${location.name}'s menu`,
    listItems("Names that differ", location.differences?.renamed)
].filter(Boolean).join("\n");

/**
 * Compact location detail for the location menu sync status card: cycles through the
 * location names on one line and only lists the locations that need attention.
 */
export default function LocationSyncDetails(props: LocationSyncDetailsProps) {
    const [tickerIndex, setTickerIndex] = useState(0);
    const [showAllIssues, setShowAllIssues] = useState(false);

    // names only cycle while syncing; once complete the summary says it all and only problems are listed
    const tickerLocations = props.status === "syncing" ? props.locations : [];
    const issues = props.status === "syncing" ? [] : props.locations.filter(l => l.status === "differs" || l.status === "error");
    const visibleIssues = showAllIssues ? issues : issues.slice(0, MAX_ISSUE_CHIPS);

    useEffect(() => {
        setTickerIndex(0);
        if (tickerLocations.length < 2) return;
        const interval = window.setInterval(() => setTickerIndex(index => index + 1), TICKER_INTERVAL_MS);
        return () => window.clearInterval(interval);
    }, [props.status, tickerLocations.length]);

    if (!props.locations.length) return null;

    const current = tickerLocations.length ? tickerLocations[tickerIndex % tickerLocations.length] : null;

    return (
        <Details onClick={(e) => e.stopPropagation()}>
            {current &&
                <Ticker aria-hidden="true">
                    <TickerItem key={`${props.status}-${tickerIndex}`}>
                        <TickerSpinner />
                        <TickerName>{current.name}</TickerName>
                        {current.isPrimary && <PrimaryTag>Primary</PrimaryTag>}
                    </TickerItem>
                </Ticker>
            }
            {issues.length > 0 &&
                <Issues>
                    <IssuesLabel>Doesn't match the primary</IssuesLabel>
                    <IssueChips>
                        {visibleIssues.map(location =>
                            <IssueChip
                                key={location.liveApplicationId ?? location.name}
                                href={location.liveApplicationId ? getLocationMenuUrl(location.liveApplicationId) : undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={getIssueTooltip(location)}
                            >
                                <IssueName>{location.name}</IssueName>
                                <IssueDetail>{describeDifferences(location)}</IssueDetail>
                            </IssueChip>
                        )}
                        {issues.length > MAX_ISSUE_CHIPS &&
                            <MoreChip type="button" onClick={() => setShowAllIssues(!showAllIssues)}>
                                {showAllIssues ? "Show less" : `+${issues.length - MAX_ISSUE_CHIPS} more`}
                            </MoreChip>
                        }
                    </IssueChips>
                </Issues>
            }
        </Details>
    )
}

const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: default;
`;

const Ticker = styled.div`
    height: 20px;
    overflow: hidden;
`;

const TickerItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    line-height: 20px;
    color: ${color_shades_darker};
    animation: locationTickerIn 0.35s ease-out;

    @keyframes locationTickerIn {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const TickerName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TickerSpinner = styled.span`
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border: 2px solid ${color_variants_ocean_light_1};
    border-top-color: ${color_colors_ocean};
    border-radius: 50%;
    animation: locationTickerSpin 0.8s linear infinite;

    @keyframes locationTickerSpin {
        to { transform: rotate(360deg); }
    }
`;

const PrimaryTag = styled.span`
    flex-shrink: 0;
    padding: 0 6px;
    border-radius: 8px;
    background: ${color_variants_ocean_light_1};
    color: ${color_colors_ocean};
    font-size: 11px;
    line-height: 16px;
`;

const Issues = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const IssuesLabel = styled.span`
    font-size: 12px;
    line-height: 16px;
    color: ${color_shades_darker};
`;

const IssueChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

const IssueChip = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 2px 8px;
    border-radius: 12px;
    border: 1px solid ${color_colors_gold};
    background: white;
    color: ${color_shades_darkest};
    font-size: 12px;
    line-height: 16px;
    text-decoration: none;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover {
        border-color: ${color_colors_ocean};
    }
`;

const IssueName = styled.span`
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const IssueDetail = styled.span`
    flex-shrink: 0;
    color: ${color_shades_darker};
`;

const MoreChip = styled.button`
    padding: 2px 8px;
    border: none;
    border-radius: 12px;
    background: none;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;
`;
